import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";
import { Badge } from "../badge";
import { nombreCliente, contactoCliente, resumenCliente, clientesAdicionalesDe } from "../../utils/clientes";
import ClienteCreateDialog from "./ClienteCreateDialog";
import ClientesWizard from "./ClientesWizard";
import { agruparEventosSinFicha, resumenBackfill } from "../../utils/clientesBackfill";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";
const fmtMoney = (v) => "$" + (Number(v) || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });

// Lista de clientes del venue (FRA-354): quién es, cómo contactarlo,
// cuántos eventos ha hecho y cuánto vale la relación.
const ClientesDirectorio = () => {
  const navigate = useNavigate();
  const { selectedVenueId, clientes, clientesLoading, events, payments, contactForms } = useVenue();
  // Leads por cliente: cuántas veces ha cotizado, cerrado o no. Es lo que
  // dice si alguien vuelve, sin necesidad de etiquetarlo.
  const leadsPorCliente = useMemo(() => {
    const out = {};
    (contactForms || []).forEach((f) => {
      if (f && !f.deleted && f.clienteId) out[f.clienteId] = (out[f.clienteId] || 0) + 1;
    });
    return out;
  }, [contactForms]);
  // Eventos donde el cliente participa sin ser el principal (la novia, quien
  // paga): cuentan en su número de eventos, aunque el dinero vaya en el
  // principal.
  const participacionesPorCliente = useMemo(() => {
    const out = {};
    (events || []).forEach((e) => {
      clientesAdicionalesDe(e).forEach(({ clienteId }) => {
        out[clienteId] = (out[clienteId] || 0) + 1;
      });
    });
    return out;
  }, [events]);
  const { userInfo } = useAuth();
  // Inversionista (P-FRA-13): la lista se consulta, pero el alta y el
  // asistente de agrupación (que crea fichas y liga eventos) no se ofrecen.
  const soloLectura = esSoloLectura(userInfo);
  const [search, setSearch] = useState("");
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Lo que el directorio todavía no sabe: eventos que ya guardan a quién le
  // vendiste, pero sin ficha. El aviso es la única forma de que alguien se
  // entere de que se puede armar.
  const pendientes = useMemo(
    () => resumenBackfill(agruparEventosSinFicha(events, clientes)),
    [events, clientes]
  );

  const filas = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (clientes || [])
      .filter((c) => (mostrarArchivados ? !!c.archivedAt : !c.archivedAt))
      .filter((c) =>
        term
          ? [nombreCliente(c), contactoCliente(c), c.correo, c.telefono, c.nombreEmpresa]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(term))
          : true
      )
      .map((c) => ({ cliente: c, resumen: resumenCliente({ cliente: c, events, payments }) }))
      .sort((a, b) => nombreCliente(a.cliente).localeCompare(nombreCliente(b.cliente), "es", { sensitivity: "base" }));
  }, [clientes, events, payments, search, mostrarArchivados]);

  if (!selectedVenueId) {
    return <div className="p-8 text-center text-zinc-500">Selecciona un venue para ver sus clientes.</div>;
  }

  return (
    <div className="p-4 space-y-4">
      {pendientes.personas > 0 && !soloLectura && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 px-4 py-3 flex flex-wrap items-center gap-3">
          <SparklesIcon className="h-5 w-5 text-amber-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {pendientes.eventos} {pendientes.eventos === 1 ? "evento" : "eventos"} sin cliente
              {pendientes.recurrentes > 0 && (
                <>
                  {" "}
                  · {pendientes.recurrentes}{" "}
                  {pendientes.recurrentes === 1 ? "cliente te repitió" : "clientes te repitieron"}
                </>
              )}
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-400">
              Esos eventos ya registran a quién le vendiste, pero por separado. El
              asistente los agrupa por persona para que confirmes cada cliente, y con eso
              el panel ya puede decirte quién ha vuelto y cuánto vale cada relación.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="ml-auto shrink-0 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            Armar el directorio
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, empresa, correo o teléfono..."
            className="w-full rounded-lg border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          />
        </div>
        <button
          type="button"
          onClick={() => setMostrarArchivados((v) => !v)}
          className="text-xs font-medium text-zinc-500 hover:text-zinc-700"
        >
          {mostrarArchivados ? "Ver activos" : "Ver archivados"}
        </button>
        {pendientes.personas === 0 && !soloLectura && (
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="text-xs font-medium text-zinc-500 hover:text-zinc-700"
            title="Revisar eventos sin cliente y clientes duplicados"
          >
            Revisar el directorio
          </button>
        )}
        {!soloLectura && (
        <button
          onClick={() => setCreateOpen(true)}
          className="ml-auto px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
          style={{ backgroundColor: ACCENT }}
        >
          <PlusIcon className="h-4 w-4" />
          Nuevo cliente
        </button>
        )}
      </div>

      {clientesLoading ? (
        <div className="text-center py-12 text-zinc-400">Cargando...</div>
      ) : filas.length === 0 ? (
        <div className="text-center py-16 px-4">
          <UserGroupIcon className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
          <div className="text-zinc-500">
            {(clientes || []).length === 0
              ? "Aún no hay clientes. Se crean solos al convertir un lead o al crear un evento, o aquí mismo. Si ya llevas eventos hechos, el asistente arma el directorio a partir de ellos."
              : "Ningún cliente coincide."}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 dark:bg-zinc-800/40">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Cliente</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Contacto</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Eventos</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Leads</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Valor total</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Pagado</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Debe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {filas.map(({ cliente: c, resumen: r }) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/dashboard/clientes/${c.id}`)}
                  className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
                >
                  <td className="px-4 py-2.5 font-medium text-zinc-900 dark:text-white">
                    <div className="flex items-center gap-2">
                      {nombreCliente(c)}
                      {c.clientType === "moral" && <Badge color="zinc">Empresa</Badge>}
                    </div>
                    {contactoCliente(c) && (
                      <div className="text-xs text-zinc-400">{contactoCliente(c)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500">
                    <div className="leading-tight">
                      {c.correo && <div>{c.correo}</div>}
                      {c.telefono && <div className="text-xs">{c.telefono}</div>}
                      {!c.correo && !c.telefono && <span className="text-zinc-300 italic">—</span>}
                    </div>
                  </td>
                  <td
                    className="px-4 py-2.5 text-right text-zinc-700 dark:text-zinc-300"
                    title={
                      participacionesPorCliente[c.id]
                        ? `${r.eventos.length} como cliente principal · ${participacionesPorCliente[c.id]} donde participa`
                        : undefined
                    }
                  >
                    {r.eventos.length + (participacionesPorCliente[c.id] || 0) || <span className="text-zinc-300">0</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right text-zinc-700 dark:text-zinc-300">
                    {leadsPorCliente[c.id] || <span className="text-zinc-300">0</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap text-zinc-900 dark:text-white">
                    {r.valorTotal > 0 ? fmtMoney(r.valorTotal) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap text-emerald-600">
                    {r.pagado > 0 ? fmtMoney(r.pagado) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {r.debe > 0 ? (
                      <span className="font-medium text-amber-600">{fmtMoney(r.debe)}</span>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!soloLectura && (
        <>
          <ClientesWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

          <ClienteCreateDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreated={(id) => navigate(`/dashboard/clientes/${id}`)}
          />
        </>
      )}
    </div>
  );
};

export default ClientesDirectorio;
